/**
 * Correo → Configuración: lo que antes vivía en `politica.yaml`, ahora en base
 * y editable por administradores (el servidor manda 403 a quien no lo es; aquí
 * sólo se esconden los botones). Cada cambio queda en la auditoría.
 *
 * Reglas que NO se tocan desde aquí: el envío sigue apagado, todo correo a un
 * destinatario externo pasa por Aprobaciones aunque esté en lista blanca, y el
 * contenido de un correo es DATO, nunca instrucciones para AROC.
 */
import { Loader2 } from "lucide-react";
import { useState } from "react";

import { ApiError } from "@/api/client";
import {
  type AjustesCorreo,
  type ConfigCorreo,
  type DominioPorRevisar,
  type FilaEscalacion,
  useAjustesCorreo,
  useAuditoriaCorreo,
  useEscalacion,
  useEscribirCorreo,
  usePlantillas,
  usePorRevisar,
  useRemitentes,
  useSugerencias,
} from "@/hooks/useCorreo";

const CASOS: Record<string, string> = {
  consulta: "Consulta",
  aviso_falla: "Aviso de falla",
  pedido_srp: "Pedido de SRP",
  protrans_tracking: "Protrans",
  asignacion_sim: "Asignación de SIM",
};
const LISTAS: Record<string, string> = {
  blanca: "Aceptado (se interpreta)",
  negra: "Basura (se ignora)",
  registrar: "Sólo registrar",
};
const SUGERENCIA: Record<string, string> = {
  basura: "Basura",
  cliente: "Cliente",
  proveedor: "Proveedor",
  interno: "Interno",
};

const cuando = (iso: string) =>
  new Date(iso).toLocaleString("es-MX", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "America/Mexico_City",
  });

const inp = "rounded border px-2 py-1 text-sm";
const btn =
  "rounded border px-2 py-0.5 text-xs hover:bg-accent disabled:opacity-40";

function Seccion({
  titulo,
  children,
}: {
  titulo: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2 rounded-lg border bg-card p-3">
      <h2 className="font-medium">{titulo}</h2>
      {children}
    </section>
  );
}

function ErrorDe({ error }: { error: unknown }) {
  if (!error) return null;
  const e = error as ApiError;
  const msg =
    e.status === 403
      ? "Sólo un administrador puede cambiar la configuración."
      : e.status === 422
        ? "Dato inválido: revisa el formato."
        : "No se pudo guardar.";
  return <p className="text-xs text-destructive">{msg}</p>;
}

function Casos({
  valor,
  onChange,
}: {
  valor: string[];
  onChange: (v: string[]) => void;
}) {
  return (
    <span className="inline-flex flex-wrap gap-2">
      {Object.entries(CASOS).map(([k, n]) => (
        <label key={k} className="inline-flex items-center gap-1 text-xs">
          <input
            type="checkbox"
            checked={valor.includes(k)}
            onChange={(e) =>
              onChange(
                e.target.checked ? [...valor, k] : valor.filter((x) => x !== k),
              )
            }
          />
          {n}
        </label>
      ))}
    </span>
  );
}

function PorRevisar({ editar }: { editar: boolean }) {
  const q = usePorRevisar();
  const w = useEscribirCorreo();
  const [reprocesar, setReprocesar] = useState(true);
  const decidir = (d: DominioPorRevisar, decision: string) =>
    w.mutate({
      method: "POST",
      ruta: "por_revisar/decidir",
      body: {
        dominio: d.dominio,
        decision,
        reprocesar: decision === "aceptar" && reprocesar,
      },
    });
  return (
    <Seccion titulo="Dominios por revisar">
      <p className="text-xs text-muted-foreground">
        Todo correo se recibe y se guarda. El de un remitente que nadie ha
        aceptado espera aquí: AROC no lo interpreta, no gasta modelo y no lo
        escala hasta que un administrador decida. La sugerencia sale de reglas
        fijas (autenticación SPF/DKIM/DMARC, dominio de cliente conocido,
        palabras de correo masivo), sin modelo.
      </p>
      {editar && (
        <label className="inline-flex items-center gap-1 text-xs">
          <input
            type="checkbox"
            checked={reprocesar}
            onChange={(e) => setReprocesar(e.target.checked)}
          />
          Al aceptar, reprocesar los correos retenidos de ese dominio
        </label>
      )}
      {q.isPending ? (
        <Loader2 className="size-4 animate-spin" />
      ) : !q.data?.length ? (
        <p className="text-sm text-muted-foreground">Nada por revisar.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr>
              <th>Dominio</th>
              <th>Correos</th>
              <th>Primero / último</th>
              <th>Asunto de muestra</th>
              <th>Sugerencia de AROC</th>
              {editar && <th />}
            </tr>
          </thead>
          <tbody>
            {q.data.map((d) => (
              <tr key={d.dominio} className="border-t align-top">
                <td className="py-1">
                  <code>{d.dominio}</code>
                  <div className="text-xs text-muted-foreground">
                    {d.remitentes.slice(0, 3).join(", ")}
                  </div>
                </td>
                <td>{d.correos}</td>
                <td className="text-xs">
                  {cuando(d.primero)}
                  <br />
                  {cuando(d.ultimo)}
                </td>
                <td className="max-w-64 truncate" title={d.asunto_muestra}>
                  {d.asunto_muestra}
                </td>
                <td className="text-xs">
                  <b>{SUGERENCIA[d.sugerencia.tipo]}</b>
                  <div className="text-muted-foreground">
                    {d.sugerencia.motivos.join(" · ")}
                  </div>
                </td>
                {editar && (
                  <td className="space-x-1 whitespace-nowrap">
                    <button
                      className={btn}
                      disabled={w.isPending}
                      onClick={() => decidir(d, "aceptar")}
                    >
                      Aceptar
                    </button>
                    <button
                      className={btn}
                      disabled={w.isPending}
                      onClick={() => decidir(d, "basura")}
                    >
                      Basura
                    </button>
                    <button
                      className={btn}
                      disabled={w.isPending}
                      onClick={() => decidir(d, "registrar")}
                    >
                      Sólo registrar
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <ErrorDe error={w.error} />
    </Seccion>
  );
}

function Remitentes({ editar }: { editar: boolean }) {
  const q = useRemitentes();
  const sug = useSugerencias();
  const w = useEscribirCorreo();
  const [patron, setPatron] = useState("");
  const [lista, setLista] = useState("blanca");
  const [casos, setCasos] = useState<string[]>(["consulta", "aviso_falla"]);
  const [notas, setNotas] = useState("");
  const alta = (p: string, l: string, c: string[], n?: string) =>
    w.mutate({
      method: "POST",
      ruta: "remitentes",
      body: {
        patron: p,
        lista: l,
        casos: l === "blanca" ? c : [],
        notas: n || null,
      },
    });
  return (
    <Seccion titulo="Remitentes: aceptados, basura y sólo registrar">
      <p className="text-xs text-muted-foreground">
        Por correo exacto (<code>persona@cliente.com</code>) o por dominio (
        <code>@cliente.com</code>). El correo exacto gana al dominio; a igual
        nivel, basura gana. Aceptar a alguien NO le manda nada: toda respuesta
        hacia fuera de tilatina.com sigue pasando por Aprobaciones.
      </p>
      <table className="w-full text-sm">
        <tbody>
          {(q.data ?? []).map((r) => (
            <tr key={r.id} className="border-t">
              <td className="py-1">
                <code>{r.patron}</code>
              </td>
              <td>{LISTAS[r.lista]}</td>
              <td className="text-xs">
                {r.casos.map((c) => CASOS[c] ?? c).join(", ")}
              </td>
              <td className="text-xs text-muted-foreground">{r.notas}</td>
              {editar && (
                <td className="text-right">
                  <button
                    className={btn}
                    onClick={() =>
                      w.mutate({ method: "DELETE", ruta: `remitentes/${r.id}` })
                    }
                  >
                    Quitar
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {editar && (
        <div className="flex flex-wrap items-center gap-2 border-t pt-2">
          <input
            className={inp}
            placeholder="@cliente.com o persona@cliente.com"
            value={patron}
            onChange={(e) => setPatron(e.target.value)}
          />
          <select
            className={inp}
            value={lista}
            onChange={(e) => setLista(e.target.value)}
          >
            {Object.entries(LISTAS).map(([k, n]) => (
              <option key={k} value={k}>
                {n}
              </option>
            ))}
          </select>
          {lista === "blanca" && <Casos valor={casos} onChange={setCasos} />}
          <input
            className={inp}
            placeholder="nota"
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
          />
          <button
            className={btn}
            disabled={!patron.trim() || w.isPending}
            onClick={() => {
              alta(patron, lista, casos, notas);
              setPatron("");
              setNotas("");
            }}
          >
            Agregar
          </button>
        </div>
      )}
      {!!sug.data?.length && (
        <div className="border-t pt-2 text-xs">
          <div className="mb-1 font-medium">
            Propuesta: dominios de clientes que AROC ya conoce (agenda de
            contactos)
          </div>
          {sug.data.map((s) => (
            <div key={s.dominio} className="flex items-center gap-2">
              <code>{s.dominio}</code>
              <span className="text-muted-foreground">
                {s.cliente} · {s.contactos} contactos
              </span>
              {editar && (
                <button
                  className={btn}
                  onClick={() =>
                    alta(
                      s.dominio,
                      "blanca",
                      ["consulta", "aviso_falla"],
                      `cliente ${s.cliente}`,
                    )
                  }
                >
                  Aceptar
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <ErrorDe error={w.error} />
    </Seccion>
  );
}

function QueSeRecibe({ editar }: { editar: boolean }) {
  const q = useAjustesCorreo();
  if (!q.data) return null;
  return <FormAjustes inicial={q.data} editar={editar} />;
}

function FormAjustes({
  inicial,
  editar,
}: {
  inicial: AjustesCorreo;
  editar: boolean;
}) {
  const w = useEscribirCorreo();
  const [a, setA] = useState(inicial);
  const lista = (v: string) =>
    v
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
  return (
    <Seccion titulo="Qué se recibe y qué no">
      <div className="grid gap-2 text-sm">
        <div>
          Casos que AROC atiende:{" "}
          <Casos
            valor={a.casos_habilitados}
            onChange={(v) => setA({ ...a, casos_habilitados: v })}
          />
        </div>
        <label>
          Tamaño máximo (KB, 0 = sin tope):{" "}
          <input
            type="number"
            min={0}
            className={`${inp} w-28`}
            value={a.tamano_max_kb}
            onChange={(e) =>
              setA({ ...a, tamano_max_kb: Number(e.target.value) })
            }
          />
        </label>
        <label>
          Correos con adjuntos:{" "}
          <select
            className={inp}
            value={a.adjuntos}
            onChange={(e) =>
              setA({
                ...a,
                adjuntos: e.target.value as AjustesCorreo["adjuntos"],
              })
            }
          >
            <option value="aceptar">se interpretan</option>
            <option value="registrar">sólo se registran</option>
            <option value="descartar">se descartan</option>
          </select>
        </label>
        <label>
          Descartar si el asunto contiene (separado por comas):{" "}
          <input
            className={`${inp} w-full`}
            defaultValue={a.asunto_descartar.join(", ")}
            onBlur={(e) =>
              setA({ ...a, asunto_descartar: lista(e.target.value) })
            }
          />
        </label>
        <label>
          SRPs que se pueden pedir por correo (ids, separados por comas):{" "}
          <input
            className={`${inp} w-full`}
            defaultValue={a.srps_por_correo.join(", ")}
            onBlur={(e) =>
              setA({ ...a, srps_por_correo: lista(e.target.value) })
            }
          />
        </label>
        <div className="text-xs text-muted-foreground">
          Copias fijas por caso:{" "}
          {Object.entries(a.cc_por_caso)
            .map(([k, v]) => `${CASOS[k] ?? k}: ${v.join(", ")}`)
            .join(" · ") || "ninguna"}
        </div>
      </div>
      {editar && (
        <button
          className={btn}
          disabled={w.isPending}
          onClick={() =>
            w.mutate({
              method: "PUT",
              ruta: "ajustes",
              body: {
                casos_habilitados: a.casos_habilitados,
                tamano_max_kb: a.tamano_max_kb,
                adjuntos: a.adjuntos,
                asunto_descartar: a.asunto_descartar,
                srps_por_correo: a.srps_por_correo,
              },
            })
          }
        >
          Guardar
        </button>
      )}
      <ErrorDe error={w.error} />
    </Seccion>
  );
}

const VACIA: Omit<FilaEscalacion, "id"> = {
  area: "",
  cliente: null,
  caso: null,
  nombre: "",
  responsables: [],
  canal: "correo",
  minutos_respuesta: null,
  minutos_escalar: null,
  activo: true,
  notas: null,
};

function Escalacion({ editar }: { editar: boolean }) {
  const q = useEscalacion();
  const w = useEscribirCorreo();
  const [f, setF] = useState(VACIA);
  const [resp, setResp] = useState("");
  const [editando, setEditando] = useState<string | null>(null);
  const guardar = () => {
    const body = {
      ...f,
      cliente: f.cliente || null,
      caso: f.caso || null,
      responsables: resp
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean),
    };
    w.mutate(
      editando
        ? { method: "PUT", ruta: `escalacion/${editando}`, body }
        : { method: "POST", ruta: "escalacion", body },
      {
        onSuccess: () => {
          setF(VACIA);
          setResp("");
          setEditando(null);
        },
      },
    );
  };
  return (
    <Seccion titulo="Tablas de escalación">
      <p className="text-xs text-muted-foreground">
        Por cliente (dominio), área y tipo de correo: a quién se escala, por qué
        canal y en qué tiempos. Gana la fila más específica. Sólo cuentas
        @tilatina.com. Los tiempos viajan en la propuesta de escalamiento;
        todavía no hay re-escalación automática al vencer.
      </p>
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-muted-foreground">
          <tr>
            <th>Área</th>
            <th>Cliente</th>
            <th>Tipo</th>
            <th>Responsable(s)</th>
            <th>Canal</th>
            <th>Respuesta / escalar (min)</th>
            {editar && <th />}
          </tr>
        </thead>
        <tbody>
          {(q.data ?? []).map((e) => (
            <tr
              key={e.id}
              className={`border-t ${e.activo ? "" : "opacity-50"}`}
            >
              <td className="py-1">{e.area}</td>
              <td>{e.cliente ?? "cualquiera"}</td>
              <td>{e.caso ? CASOS[e.caso] : "cualquiera"}</td>
              <td className="text-xs">
                {e.nombre}
                <div className="text-muted-foreground">
                  {e.responsables.join(", ") ||
                    "sin correo: escala al responsable por defecto"}
                </div>
              </td>
              <td>{e.canal}</td>
              <td>
                {e.minutos_respuesta ?? "—"} / {e.minutos_escalar ?? "—"}
              </td>
              {editar && (
                <td className="space-x-1 whitespace-nowrap text-right">
                  <button
                    className={btn}
                    onClick={() => {
                      setEditando(e.id);
                      setF({ ...e });
                      setResp(e.responsables.join(", "));
                    }}
                  >
                    Editar
                  </button>
                  <button
                    className={btn}
                    onClick={() =>
                      w.mutate({ method: "DELETE", ruta: `escalacion/${e.id}` })
                    }
                  >
                    Quitar
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {editar && (
        <div className="flex flex-wrap items-center gap-2 border-t pt-2">
          <input
            className={`${inp} w-32`}
            placeholder="área"
            value={f.area}
            onChange={(e) => setF({ ...f, area: e.target.value })}
          />
          <input
            className={`${inp} w-36`}
            placeholder="@cliente.com"
            value={f.cliente ?? ""}
            onChange={(e) => setF({ ...f, cliente: e.target.value })}
          />
          <select
            className={inp}
            value={f.caso ?? ""}
            onChange={(e) => setF({ ...f, caso: e.target.value || null })}
          >
            <option value="">cualquier tipo</option>
            {Object.entries(CASOS).map(([k, n]) => (
              <option key={k} value={k}>
                {n}
              </option>
            ))}
          </select>
          <input
            className={`${inp} w-36`}
            placeholder="nombre"
            value={f.nombre}
            onChange={(e) => setF({ ...f, nombre: e.target.value })}
          />
          <input
            className={`${inp} w-56`}
            placeholder="correos @tilatina.com, separados por comas"
            value={resp}
            onChange={(e) => setResp(e.target.value)}
          />
          <select
            className={inp}
            value={f.canal}
            onChange={(e) =>
              setF({ ...f, canal: e.target.value as FilaEscalacion["canal"] })
            }
          >
            <option value="correo">correo</option>
            <option value="telegram">Telegram</option>
            <option value="ambos">ambos</option>
          </select>
          <input
            type="number"
            min={0}
            className={`${inp} w-20`}
            placeholder="resp."
            value={f.minutos_respuesta ?? ""}
            onChange={(e) =>
              setF({
                ...f,
                minutos_respuesta: e.target.value
                  ? Number(e.target.value)
                  : null,
              })
            }
          />
          <input
            type="number"
            min={0}
            className={`${inp} w-20`}
            placeholder="escalar"
            value={f.minutos_escalar ?? ""}
            onChange={(e) =>
              setF({
                ...f,
                minutos_escalar: e.target.value ? Number(e.target.value) : null,
              })
            }
          />
          <label className="inline-flex items-center gap-1 text-xs">
            <input
              type="checkbox"
              checked={f.activo}
              onChange={(e) => setF({ ...f, activo: e.target.checked })}
            />
            activa
          </label>
          <button
            className={btn}
            disabled={!f.area || !resp.trim() || w.isPending}
            onClick={guardar}
          >
            {editando ? "Guardar cambios" : "Agregar"}
          </button>
        </div>
      )}
      <ErrorDe error={w.error} />
    </Seccion>
  );
}

function Plantillas({ editar }: { editar: boolean }) {
  const q = usePlantillas();
  const w = useEscribirCorreo();
  const [p, setP] = useState({
    clave: "",
    caso: "",
    asunto: "",
    cuerpo: "",
    activa: true,
  });
  return (
    <Seccion titulo="Respuestas tipo">
      <p className="text-xs text-muted-foreground">
        La respuesta tipo activa de un caso se usa como borrador cuando AROC no
        redactó uno, y se muestra junto al borrador en Aprobaciones. Igual que
        todo borrador, nadie la recibe sin que una persona la apruebe.
      </p>
      {(q.data ?? []).map((t) => (
        <div
          key={t.id}
          className={`border-t py-1 text-sm ${t.activa ? "" : "opacity-50"}`}
        >
          <div className="flex items-center gap-2">
            <code>{t.clave}</code>
            <span className="text-xs text-muted-foreground">
              {t.caso ? CASOS[t.caso] : "sin caso"} · {t.asunto}
            </span>
            {editar && (
              <span className="ml-auto space-x-1">
                <button
                  className={btn}
                  onClick={() => setP({ ...t, caso: t.caso ?? "" })}
                >
                  Editar
                </button>
                <button
                  className={btn}
                  onClick={() =>
                    w.mutate({ method: "DELETE", ruta: `plantillas/${t.id}` })
                  }
                >
                  Quitar
                </button>
              </span>
            )}
          </div>
          <pre className="whitespace-pre-wrap text-xs text-muted-foreground">
            {t.cuerpo}
          </pre>
        </div>
      ))}
      {editar && (
        <div className="grid gap-2 border-t pt-2">
          <div className="flex flex-wrap gap-2">
            <input
              className={`${inp} w-40`}
              placeholder="clave (a-z, 0-9, -)"
              value={p.clave}
              onChange={(e) => setP({ ...p, clave: e.target.value })}
            />
            <select
              className={inp}
              value={p.caso}
              onChange={(e) => setP({ ...p, caso: e.target.value })}
            >
              <option value="">sin caso</option>
              {Object.entries(CASOS).map(([k, n]) => (
                <option key={k} value={k}>
                  {n}
                </option>
              ))}
            </select>
            <input
              className={`${inp} flex-1`}
              placeholder="asunto"
              value={p.asunto}
              onChange={(e) => setP({ ...p, asunto: e.target.value })}
            />
            <label className="inline-flex items-center gap-1 text-xs">
              <input
                type="checkbox"
                checked={p.activa}
                onChange={(e) => setP({ ...p, activa: e.target.checked })}
              />
              activa
            </label>
          </div>
          <textarea
            className={`${inp} min-h-20`}
            placeholder="cuerpo de la respuesta"
            value={p.cuerpo}
            onChange={(e) => setP({ ...p, cuerpo: e.target.value })}
          />
          <button
            className={`${btn} justify-self-start`}
            disabled={!p.clave || !p.cuerpo.trim() || w.isPending}
            onClick={() =>
              w.mutate(
                {
                  method: "POST",
                  ruta: "plantillas",
                  body: { ...p, caso: p.caso || null },
                },
                {
                  onSuccess: () =>
                    setP({
                      clave: "",
                      caso: "",
                      asunto: "",
                      cuerpo: "",
                      activa: true,
                    }),
                },
              )
            }
          >
            Guardar respuesta tipo
          </button>
        </div>
      )}
      <ErrorDe error={w.error} />
    </Seccion>
  );
}

function Auditoria() {
  const q = useAuditoriaCorreo();
  return (
    <Seccion titulo="Quién cambió qué">
      {!q.data?.length ? (
        <p className="text-sm text-muted-foreground">
          Sin cambios registrados.
        </p>
      ) : (
        <table className="w-full text-xs">
          <tbody>
            {q.data.map((a) => (
              <tr key={a.id} className="border-t">
                <td className="py-0.5">{cuando(a.ts)}</td>
                <td>{a.usuario}</td>
                <td>{a.accion}</td>
                <td>{a.entidad}</td>
                <td>
                  <code>{a.clave}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Seccion>
  );
}

export function CorreoConfiguracion({ c }: { c: ConfigCorreo }) {
  const editar = !c.solo_lectura;
  return (
    <div className="space-y-4 text-sm">
      <div className="rounded border border-amber-300 bg-amber-50 p-2 dark:border-amber-700 dark:bg-amber-950/40">
        {editar
          ? "Puedes editar: cada cambio rige en menos de un minuto y queda en la auditoría."
          : "Sólo lectura: la configuración la cambia un administrador."}{" "}
        Lo que no cambia desde aquí: el envío sigue apagado, todo correo a un
        destinatario fuera de tilatina.com pasa por Aprobaciones aunque esté
        aceptado, y lo que dice un correo es dato, nunca una instrucción para
        AROC.
      </div>
      <PorRevisar editar={editar} />
      <Remitentes editar={editar} />
      <QueSeRecibe editar={editar} />
      <Escalacion editar={editar} />
      <Plantillas editar={editar} />
      <Seccion titulo="Topes y banderas (nube.env)">
        <div>
          Tokens: {c.topes.tokens_dia}/día, {c.topes.tokens_mes}/mes · USD{" "}
          {c.topes.usd_dia}/día, USD {c.topes.usd_mes}/mes
        </div>
        <div>
          Interpretación: {c.banderas.interpretar ? "encendida" : "apagada"} ·
          usuario de servicio: {String(c.banderas.usuario_servicio || "—")}
        </div>
        <div>
          Envío: {c.banderas.envio === "true" ? "encendido" : "apagado"} ·
          casos: {String(c.banderas.envio_casos || "ninguno")} · responde a:{" "}
          {String(c.banderas.responder_a || "—")}
        </div>
      </Seccion>
      <Auditoria />
    </div>
  );
}
